// Swapping which Descope session this browser is holding — the step that turns
// an admin's tab into the customer's app, and back again.
//
// The Descope SDK owns its own storage; there is no public "adopt this refresh
// token" call, so view-as writes into the SDK's slots and reloads, letting the
// SDK mint the session token from the token it finds. Kept free of any SDK
// import so it stays testable on its own — the wiring lives in
// `features/auth/descope.ts`.

/** The SDK's key names when it runs without a storage prefix — the shape our own
 *  app configures (main.tsx passes only `projectId`). Used as a floor, never as
 *  an assumption: see `slotHolding`. */
export const DEFAULT_REFRESH_SLOT = 'DSR';
export const DEFAULT_SESSION_SLOT = 'DS';

/** The three reads the swap needs. Narrower than `Storage` so a test can stand
 *  in a Map and so nothing here can reach for the rest of the API. */
type ReadableStorage = Pick<Storage, 'length' | 'key' | 'getItem'>;
type WritableStorage = ReadableStorage & Pick<Storage, 'setItem' | 'removeItem'>;

/**
 * The key currently holding `value`, or `fallback` if none does.
 *
 * Finding the slot by its CONTENT rather than by its name is what keeps this
 * working when the SDK prefixes its keys (it builds them as `<prefix>DSR`, and
 * the prefix is its own business). An empty `value` never matches — with no
 * token to look for, the first empty-valued key would otherwise be mistaken for
 * the slot and swallow the new session.
 *
 * ponytail: reaching into another library's storage, because its public
 * `refresh(token)` persists a passed token only when Descope token rotation is
 * on — silently leaving the admin's token in place otherwise, so the tab would
 * drift back to the admin at the next refresh tick. Drop all of this the day the
 * SDK grows a real "use this session" call.
 */
function slotHolding(storage: ReadableStorage, value: string): string | null {
  if (!value) return null;
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && storage.getItem(key) === value) return key;
  }
  return null;
}

/** Where the SDK remembers WHO signed in last, so the login screen can offer
 *  "continue as …". Unprefixed, and written from every `me()` response — so a
 *  view-as session overwrites them with the CUSTOMER, and the admin's own sign-in
 *  page would then greet them by the customer's name and address. Cleared on
 *  every swap: losing the shortcut costs a click, leaking the name does not. */
export const IDENTITY_HINT_SLOTS = [
  'dls_last_user_login_id',
  'dls_last_user_display_name',
  'dls_last_auth',
] as const;

/** Forget who signed in last. Called on the way into a session and again as a
 *  view-as tab goes away: clearing at the swap alone is not enough, because the
 *  SDK rewrites these from the `me()` it fires on the next boot — with the
 *  CUSTOMER in them. Verified live: after a swap they read the customer's name. */
export function clearIdentityHints(storage: Pick<Storage, 'removeItem'>): void {
  for (const slot of IDENTITY_HINT_SLOTS) storage.removeItem(slot);
}

/**
 * Point this browser's Descope session at `nextRefreshJwt`.
 *
 * Returns whether the refresh slot was found by the token it was HOLDING, i.e.
 * whether this really replaced the session the SDK reads. False means the slot
 * had to be guessed and the swap may be a no-op — the caller must not navigate
 * on that, or the reload comes back as the previous identity with nothing on
 * screen to say so.
 *
 * The session token is REMOVED rather than replaced: we only ever hold a refresh
 * token (that is what the server mints), and leaving the previous session token
 * behind would bring the app up as the previous identity until it happened to
 * refresh. Gone, the SDK mints a matching one on boot.
 *
 * Both slots are resolved BEFORE anything is written: a `setItem` that falls back
 * ADDS a key, which shifts every index a later scan would walk — so locating the
 * session slot afterwards could miss it and leave the old token to win.
 */
export function rewriteSessionSlots(
  storage: WritableStorage,
  currentRefreshJwt: string,
  currentSessionJwt: string,
  nextRefreshJwt: string,
): boolean {
  const refreshSlot = slotHolding(storage, currentRefreshJwt);
  const sessionSlot = slotHolding(storage, currentSessionJwt);
  storage.setItem(refreshSlot ?? DEFAULT_REFRESH_SLOT, nextRefreshJwt);
  storage.removeItem(sessionSlot ?? DEFAULT_SESSION_SLOT);
  clearIdentityHints(storage);
  return refreshSlot !== null;
}
